import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useCopy } from '../hooks/useCopy';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const { text } = useCopy('contact');
  const [customRequest, setCustomRequest] = useState({
    name: '',
    email: '',
    message: '',
    reference: 'Contact Form Request',
    website: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const blurVariants = {
    hidden: { filter: 'blur(0px)' },
    visible: { filter: 'blur(5px)', transition: { delay: 0.33, duration: 0.25 } }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { delay: 0.7, duration: 0.25 } }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/notify?action=custom-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customRequest)
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      setSubmitSuccess(true);
      setCustomRequest({
        name: '',
        email: '',
        message: '',
        reference: 'Contact Form Request',
        website: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch (error) {
      setSubmitError(t('contact.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
      />
      <div className="w-screen">
        {/* Hero */}
        <section className="relative">
          <motion.div
            className="absolute top-0 left-1/2 -translate-x-1/2 min-w-[100vw] h-[calc(100%+89px)] -mt-[89px] bg-cover bg-left md:bg-center scale-110 -z-10"
            style={{
              backgroundImage: "url('/PERLAS.jpg')",
              backgroundPosition: '10% 70%', // x y — adjust to move the image
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat'
            }}
            initial="hidden"
            animate="visible"
            variants={blurVariants}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent dark:from-black/50" />
          </motion.div>

          <div className="relative py-14 md:py-20 px-6 md:px-4">
            <div className="max-w-[90vw] md:max-w-[min(80vw,1400px)] mx-auto">
              <motion.h1
                className="text-4xl md:text-6xl font-bold text-dark dark:text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
                initial="hidden"
                animate="visible"
                variants={textVariants}
              >
                {text('contact-hero-title', 'Get in Touch')}
              </motion.h1>
              <motion.p
                className="mt-4 text-lg md:text-xl text-dark dark:text-white max-w-xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] dark:drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                initial="hidden"
                animate="visible"
                variants={textVariants}
              >
                {text('contact-hero-subtitle', "Have a question or a vision for a custom piece? I'd love to hear from you.")}
              </motion.p>
            </div>
          </div>
        </section>

        {/* Form + Image side by side */}
        <section className="py-14 md:py-20 px-0 md:px-0">
          <div className="w-full grid md:grid-cols-2 gap-10 md:gap-12 items-start">
            {/* Form column */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="pl-6 pr-6 md:pl-12 md:pr-8"
            >
              {/* Contact links */}
              <div className="flex flex-wrap gap-4 md:gap-6 mb-8 md:mb-10">
                <a
                  href="mailto:hello@camilaparente.com"
                  className="text-base md:text-lg text-black/80 dark:text-white/80 hover:text-brand-blue dark:hover:text-brand-blue transition-colors"
                >
                  hello@camilaparente.com
                </a>
                <a
                  href="https://www.instagram.com/camilaparente___/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-base md:text-lg text-black/80 dark:text-white/80 hover:text-brand-blue dark:hover:text-brand-blue transition-colors"
                >
                  <FontAwesomeIcon icon={faInstagram} />
                  @camilaparente___
                </a>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-7 md:space-y-8">
                {/* Honeypot field */}
                <input
                  type="text"
                  name="website"
                  value={customRequest.website}
                  onChange={(e) => setCustomRequest({ ...customRequest, website: e.target.value })}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div>
                  <label className="block text-sm md:text-base tracking-widest uppercase text-black/70 dark:text-white/70 mb-2">
                    {t('contact.name')}
                  </label>
                  <input
                    type="text"
                    value={customRequest.name}
                    onChange={(e) => setCustomRequest({ ...customRequest, name: e.target.value })}
                    className="w-full bg-transparent border-b border-black/30 dark:border-white/30 py-3 text-base md:text-lg text-black dark:text-white focus:border-brand-blue focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm md:text-base tracking-widest uppercase text-black/70 dark:text-white/70 mb-2">
                    {t('contact.email')}
                  </label>
                  <input
                    type="email"
                    value={customRequest.email}
                    onChange={(e) => setCustomRequest({ ...customRequest, email: e.target.value })}
                    className="w-full bg-transparent border-b border-black/30 dark:border-white/30 py-3 text-base md:text-lg text-black dark:text-white focus:border-brand-blue focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm md:text-base tracking-widest uppercase text-black/70 dark:text-white/70 mb-2">
                    {t('contact.message')}
                  </label>
                  <textarea
                    value={customRequest.message}
                    onChange={(e) => setCustomRequest({ ...customRequest, message: e.target.value })}
                    rows={5}
                    className="w-full bg-transparent border-b border-black/30 dark:border-white/30 py-3 text-base md:text-lg text-black dark:text-white focus:border-brand-blue focus:outline-none transition-colors resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="bg-brand-blue text-white text-base md:text-lg py-2.5 px-6 rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : t('contact.send')}
                </button>

                {submitSuccess && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-green-600 dark:text-green-400 text-base"
                  >
                    {t('contact.sent')}
                  </motion.p>
                )}

                {submitError && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-600 dark:text-red-400 text-base"
                  >
                    {submitError}
                  </motion.p>
                )}
              </form>
            </motion.div>

            {/* Image column */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="h-[50vh] md:h-[40vh] w-full overflow-hidden md:-mt-10"
            >
              <img
                src="/poldancera cuerda excelcior.jpg"
                alt="Poledancer earing, close up shot, product photography"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Custom Work section */}
        <section className="w-full -mt-10">
          <div className="grid md:grid-cols-2">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="h-[50vh] md:h-[50vh] overflow-hidden order-2 md:order-1"
            >
              <img
                src="/TOROCONPERLAS.jpg"
                alt="Custom jewelry piece, Ring of a bull with pearls in the background"
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-col justify-center px-6 md:px-16 py-10 md:py-12 order-1 md:order-2"
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-black dark:text-white mb-4">
                {text('contact-custom-title', 'Custom Work')}
              </h2>
              <p className="text-base md:text-lg text-black/90 dark:text-white/90 leading-relaxed mb-6">
                {text('contact-custom-desc', "Every custom piece begins with a conversation. Share your vision — a sketch, a reference, or just an idea — and I'll guide the process from concept to finished piece, crafted by hand in my Rome workshop.")}
              </p>
              <Link
                to={localePath('/shop')}
                className="inline-block bg-brand-blue text-white text-base md:text-lg py-2.5 px-6 rounded-lg hover:bg-brand-dark transition-colors w-fit"
              >
                {t('contact.browse')}
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
